import { Card, Flex, Spinner, TabPanel, TabProps, TabsLayout, useBreakpoint } from '@televet/kibble-ui';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import React, { useMemo } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { RouteBasePaths } from 'routes';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import useClinicUser from 'shared/hooks/useClinicUser';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import {
  SortOrder,
  useFindManyAppointmentNoteTemplateQuery,
  useGetAppointmentAiRecapInfoQuery,
  useRecapChangedSubscription,
  useSubscribeToRecapSummaryGeneratedSubscription,
} from 'shared/types/graphql';
import AppointmentSummary from './components/AppointmentSummary';
import NotesTab from './components/NotesTab';
import RecordingItem from './components/RecordingItem';
import TranscriptTab from './components/TranscriptTab';

type AppointmentNotesParams = {
  appointmentId: string;
};

const AppointmentRecapNotes = ({ appointmentId }: AppointmentNotesParams): JSX.Element => {
  const history = useHistory();

  const { currentClinicId } = useClinicUser();
  const { isFeatureEnabled, isFeatureFlagsLoading } = useFeatureFlag();
  const currentBreakpoint = useBreakpoint();
  const { isMobile } = useResolutionProvider();

  const {
    data,
    loading,
    refetch: refetchRecapInfo,
    called,
  } = useGetAppointmentAiRecapInfoQuery({
    variables: {
      appointmentId,
      orderBy: [
        {
          createdAt: SortOrder.Desc,
        },
      ],
    },
    skip:
      isFeatureFlagsLoading ||
      !isFeatureEnabled(FeatureFlagName.AppointmentsPageImprovements) ||
      !appointmentId ||
      !currentClinicId,
  });

  const { data: appointmentNoteTemplateData, refetch: refetchNoteTemplates } = useFindManyAppointmentNoteTemplateQuery({
    variables: { where: { clinicId: { equals: currentClinicId }, isDeleted: { equals: false } } },
    skip: !currentClinicId,
  });

  useRecapChangedSubscription({
    variables: {
      where: {
        node: {
          appointmentId: {
            equals: appointmentId,
          },
        },
      },
    },
    skip: !appointmentId,
    onData: () => {
      refetchRecapInfo();
      refetchNoteTemplates();
    },
  });

  useSubscribeToRecapSummaryGeneratedSubscription({
    variables: {
      where: {
        appointmentId,
      },
    },
    skip: !appointmentId,
    onData: () => {
      refetchRecapInfo();
      refetchNoteTemplates();
    },
  });

  const appointmentData = data?.findUniqueAppointment;
  const recordings = data?.findManyRecap;

  const tabList: TabProps[] = useMemo(
    () => [
      {
        label: 'Notes',
        color: 'text.default',
      },
      {
        label: 'Transcript',
        color: 'text.default',
      },
    ],
    [],
  );

  if (!isFeatureFlagsLoading && !isFeatureEnabled(FeatureFlagName.AppointmentsPageImprovements)) {
    return <Redirect to={RouteBasePaths.Appointments} />;
  }

  if (!called || loading) {
    return <Spinner isFullScreen={true}/>;
  }

  if (!appointmentData || appointmentData.clinicId !== currentClinicId) {
    return (
      <Flex px={isMobile ? 3 : 8} pt={isMobile ? 3 : 8} height="100%" direction="column">
        <Button
          variant="ghostNeutral"
          size="md"
          iconName="arrowLeft"
          onClick={(): void => {
            history.push(RouteBasePaths.Appointments);
          }}
          mb={6}
          alignSelf="flex-start"
          height="auto"
        >
          Go Back to Appointments
        </Button>
        <Heading size="md">Appointment not found</Heading>
      </Flex>
    );
  }

  return (
    <Flex px={isMobile ? 3 : 8} pt={isMobile ? 3 : 8} height="100%" direction="column">
      {isMobile ? (
        <Alert
          status="info"
          description="Manage and download summary and recordings on desktop."
          hideCloseButton={true}
          color="text.default"
          mb={4}
        />
      ) : (
        <Flex direction="column">
          <Button
            variant="ghostNeutral"
            size="md"
            iconName="arrowLeft"
            onClick={(): void => {
              history.push(RouteBasePaths.Appointments);
            }}
            mb={6}
            alignSelf="flex-start"
            height="auto"
          >
            Go Back to Appointments
          </Button>
        </Flex>
      )}

      <Flex height="100%" width="100%" overflow="hidden">
        {/* Summary & Recordings */}
        <Flex height="100%" width="100%" gap={5}>
          {['xl', '2xl'].includes(currentBreakpoint || '') && (
            <Flex direction="column" width="360px" minWidth="360px" gap={5} height="100%">
              <Card>
                <AppointmentSummary appointmentData={appointmentData} />
              </Card>

              <Card overflow="auto" borderBottomRadius={recordings && recordings.length > 2 ? 0 : undefined}>
                <Heading size="sm">Recordings ({(recordings ?? []).length})</Heading>
                {(recordings ?? []).map((r, i) => (
                  <RecordingItem key={r.id} recap={r} index={i} total={(recordings ?? []).length} />
                ))}
              </Card>
            </Flex>
          )}
          {/* Tabs */}
          <Card width="100%" p={0} borderBottomRadius={0}>
            <TabsLayout
              height="100%"
              display="flex"
              flexDirection="column"
              tabList={tabList}
              tabPanelsStyle={{
                height: '100%',
              }}
              tabPanels={[
                <TabPanel
                  key="notes-tab"
                  pt={isMobile ? 3 : 6}
                  px={isMobile ? 4 : 5}
                  pb={isMobile ? 4 : 5}
                  height="100%"
                  display="flex"
                  flexDirection="column"
                >
                  <NotesTab
                    appointmentData={appointmentData}
                    appointmentNoteTemplateData={appointmentNoteTemplateData?.findManyAppointmentNoteTemplate || []}
                    latestRecording={recordings?.[0]}
                  />
                </TabPanel>,
                <TabPanel
                  key="transcript-tab"
                  pt={isMobile ? 3 : 6}
                  px={isMobile ? 4 : 5}
                  pb={isMobile ? 4 : 5}
                  height="100%"
                  overflow="auto"
                  display="flex"
                  flexDirection="column"
                >
                  <TranscriptTab recordings={recordings || []} />
                </TabPanel>,
              ]}
            />
          </Card>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default AppointmentRecapNotes;
